import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Dimensions, ScaledSize, useWindowDimensions, Platform } from 'react-native';

/**
 * Enhanced window dimensions hook that reliably updates on fold/unfold transitions
 * 
 * On native: Uses useWindowDimensions() for reactive updates
 * On web: Uses visualViewport API and Dimensions.addEventListener to separate
 * keyboard changes from real window resizes
 * 
 * Prevents re-renders from keyboard-triggered height changes on web touch devices
 * 
 * @returns {Object} Object with width and height that updates reactively
 */
export const useReactiveWindowDimensions = () => {
  // On native, use the built-in hook directly
  const { width: nativeWidth, height: nativeHeight } = useWindowDimensions();
  
  // On web, track dimensions separately using visualViewport
  const [dimensions, setDimensions] = useState<ScaledSize>(() => 
    Dimensions.get('window')
  );
  
  // Refs for web-specific logic
  const rafIdRef = useRef<number | null>(null);
  const lastUpdateRef = useRef<number>(0);
  const isInputFocusedRef = useRef(false);
  const layoutViewportHeightRef = useRef<number | null>(null);

  // Check if an input is currently focused (for keyboard detection)
  const checkInputFocus = useCallback(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') {
      return false;
    }
    
    const activeElement = document.activeElement;
    if (!activeElement) return false;
    
    const tagName = activeElement.tagName.toLowerCase();
    const isInput = tagName === 'input' || tagName === 'textarea';
    const isContentEditable = activeElement.getAttribute('contenteditable') === 'true' || 
                              activeElement.getAttribute('contenteditable') === '';
    
    return isInput || isContentEditable;
  }, []);

  // Update input focus state
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') {
      return;
    }

    const updateFocusState = () => {
      isInputFocusedRef.current = checkInputFocus();
    };

    // Listen to focus/blur events on inputs
    document.addEventListener('focusin', updateFocusState);
    document.addEventListener('focusout', updateFocusState);

    // Initial check
    updateFocusState();

    return () => {
      document.removeEventListener('focusin', updateFocusState);
      document.removeEventListener('focusout', updateFocusState);
    };
  }, [checkInputFocus]);

  // Debounced update function
  const updateDimensions = useCallback((newDims: ScaledSize, source: string) => {
    // Debounce: only update if enough time has passed since last update
    const now = Date.now();
    if (now - lastUpdateRef.current < 50) {
      // Cancel previous RAF if exists
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
      
      // Schedule update for next frame
      rafIdRef.current = requestAnimationFrame(() => {
        updateDimensions(newDims, source);
      });
      return;
    }

    lastUpdateRef.current = now;
    rafIdRef.current = null;

    setDimensions(prev => {
      // Only update if dimensions actually changed
      if (prev.width === newDims.width && prev.height === newDims.height) {
        return prev;
      }
      
      return newDims;
    });
  }, []);

  // On native platforms, use useWindowDimensions + Dimensions.addEventListener for fold/unfold
  useEffect(() => {
    if (Platform.OS !== 'web') {
      const currentDims = Dimensions.get('window');
      updateDimensions({
        width: nativeWidth,
        height: nativeHeight,
        scale: currentDims.scale,
        fontScale: currentDims.fontScale,
      }, 'useWindowDimensions');
    }
  }, [nativeWidth, nativeHeight, Platform.OS, updateDimensions]);

  // On native, also listen to Dimensions.addEventListener for fold/unfold transitions
  useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }

    const subscription = Dimensions.addEventListener('change', ({ window: dimWindow }) => {
      updateDimensions({
        width: dimWindow.width,
        height: dimWindow.height,
        scale: dimWindow.scale,
        fontScale: dimWindow.fontScale,
      }, 'Dimensions.addEventListener');
    });

    return () => {
      subscription?.remove();
    };
  }, [Platform.OS, updateDimensions]);

  // On web, use visualViewport API and Dimensions.addEventListener
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
      return;
    }

    // Initialize layout viewport height (stable, doesn't change with keyboard)
    if (layoutViewportHeightRef.current === null) {
      layoutViewportHeightRef.current = window.innerHeight;
    }

    const handleResize = () => {
      // Get layout viewport (stable, excludes keyboard)
      const layoutWidth = window.innerWidth;
      const layoutHeight = window.innerHeight;
      
      // Update layout viewport height if it increased (real resize, not keyboard)
      if (layoutHeight > (layoutViewportHeightRef.current ?? 0)) {
        layoutViewportHeightRef.current = layoutHeight;
      }

      // Use visualViewport if available (more accurate for keyboard detection)
      let finalWidth = layoutWidth;
      let finalHeight = layoutHeight;

      if (window.visualViewport) {
        const vp = window.visualViewport;
        
        // If an input is focused, use layout viewport height (ignore visualViewport height changes)
        if (isInputFocusedRef.current) {
          finalHeight = layoutViewportHeightRef.current ?? layoutHeight;
        } else {
          // No input focused - use visualViewport for accurate dimensions
          // But prefer layout viewport height if visualViewport is smaller (keyboard showing)
          if (vp.height < layoutHeight - 100) {
            // Likely keyboard showing - use layout viewport
            finalHeight = layoutViewportHeightRef.current ?? layoutHeight;
          } else {
            // No keyboard - use visualViewport for accuracy
            finalHeight = vp.height;
            // Update layout viewport height if visualViewport is larger
            if (vp.height > (layoutViewportHeightRef.current ?? 0)) {
              layoutViewportHeightRef.current = vp.height;
            }
          }
          finalWidth = vp.width;
        }
      } else {
        // Fallback: if input is focused, don't update height (likely keyboard)
        if (isInputFocusedRef.current) {
          finalHeight = layoutViewportHeightRef.current ?? layoutHeight;
        } else {
          // No input focused - check if height decreased significantly
          const heightDiff = (layoutViewportHeightRef.current ?? layoutHeight) - layoutHeight;
          if (heightDiff > 150) {
            // Likely keyboard - use stable height
            finalHeight = layoutViewportHeightRef.current ?? layoutHeight;
          } else {
            // Real resize - update layout viewport height
            if (layoutHeight > (layoutViewportHeightRef.current ?? 0)) {
              layoutViewportHeightRef.current = layoutHeight;
            }
            finalHeight = layoutHeight;
          }
        }
      }

      const currentDims = Dimensions.get('window');
      updateDimensions({
        width: finalWidth,
        height: finalHeight,
        scale: currentDims.scale,
        fontScale: currentDims.fontScale,
      }, 'resize');
    };

    // Use visualViewport API if available (preferred for keyboard detection)
    // Otherwise fall back to window resize
    let visualViewportCleanup: (() => void) | null = null;
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
      visualViewportCleanup = () => {
        window.visualViewport?.removeEventListener('resize', handleResize);
      };
    } else {
      // Fallback to window resize if visualViewport not available
      window.addEventListener('resize', handleResize);
    }

    // Initial update
    handleResize();

    return () => {
      if (visualViewportCleanup) {
        visualViewportCleanup();
      } else {
        window.removeEventListener('resize', handleResize);
      }
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [Platform.OS, updateDimensions]);

  // Memoize return value to prevent unnecessary re-renders
  return useMemo(() => ({
    width: dimensions.width,
    height: dimensions.height,
  }), [dimensions.width, dimensions.height]);
};

export default useReactiveWindowDimensions;
